import {
  Box,
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import {
  GenerateFormFields_New,
  TFormFieldsAndRules_New,
  RenderType,
  getFieldsRules_New,
} from '@componentsroot/Form/FormGenerator';
import { useCustomForm } from '@componentsroot/Form';
import { validationRules } from '@verifime/utils';
import { useState, useEffect } from 'react';

const returnUrlFormFields: TFormFieldsAndRules_New<'returnUrl'> = {
  row1: [
    {
      fieldName: 'returnUrl',
      label: 'Return URL',
      renderType: RenderType.Text,
      rules: validationRules.OPTIONAL_URL_RULES,
      helpText: 'Must start with http:// or https://',
    },
  ],
};

type TReturnUrlFormData = {
  returnUrl: string;
};

export interface ReturnPathSectionProps {
  initialReturnUrl: string;
  selectedCode: string;
  onReturnUrlSave: (returnUrl: string) => Promise<void>;
  dataTestId?: string;
}

export function ReturnPathSection({
  initialReturnUrl,
  selectedCode,
  onReturnUrlSave,
  dataTestId = 'return-path-section',
}: Readonly<ReturnPathSectionProps>) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useCustomForm<TReturnUrlFormData>({
    schema: getFieldsRules_New(returnUrlFormFields),
    defaultValues: {
      returnUrl: initialReturnUrl,
    },
  });

  // Reset form whenever initialReturnUrl changes (e.g., when switching org codes)
  useEffect(() => {
    reset({ returnUrl: initialReturnUrl });
    setIsEditing(false);
  }, [initialReturnUrl, reset]);

  const saveFormData = async (formData: TReturnUrlFormData) => {
    setIsSaving(true);
    try {
      await onReturnUrlSave(formData.returnUrl);
      setIsEditing(false);
      setShowConfirmDialog(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = handleSubmit(async (formData) => {
    if (!selectedCode) {
      return;
    }

    // If user is trying to clear an existing return URL
    if (!formData.returnUrl && initialReturnUrl) {
      setShowConfirmDialog(true);
      return;
    }

    await saveFormData(formData);
  });

  const handleCancel = () => {
    reset({ returnUrl: initialReturnUrl });
    setIsEditing(false);
  };

  return (
    <Box data-testid={dataTestId}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.semantic.spacing.sm,
        })}
      >
        <Typography variant="subtitle1">Return Path</Typography>
        {!isEditing && (
          <IconButton
            size="small"
            onClick={() => setIsEditing(true)}
            data-action="edit-return-url"
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {!isEditing ? (
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            padding: theme.semantic.spacing.sm,
            borderRadius: theme.semantic.borderRadius.sm,
            wordBreak: 'break-all',
          })}
        >
          <Typography variant="body2">{initialReturnUrl || 'No return URL set'}</Typography>
        </Box>
      ) : (
        <Box>
          <Typography
            variant="caption"
            sx={(theme) => ({
              display: 'block',
              marginBottom: theme.semantic.spacing.xs,
              color: theme.palette.text.secondary,
            })}
          >
            Enter the return URL and click Save to apply changes
          </Typography>
          <Box key={selectedCode + initialReturnUrl}>
            <GenerateFormFields_New
              formFieldsAndRules={returnUrlFormFields}
              control={control}
              errors={errors}
            />
          </Box>
          <Box
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'flex-end',
              gap: theme.semantic.spacing.sm,
              marginTop: theme.semantic.spacing.sm,
            })}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSaving}
              data-action="cancel-return-path"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!selectedCode || Boolean(errors.returnUrl) || isSaving}
              data-action="save-return-path"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      )}

      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">Remove Return URL</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Are you sure you want to not set a return URL?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)} color="error">
            Cancel
          </Button>
          <Button
            onClick={() => saveFormData({ returnUrl: '' })}
            color="primary"
            variant="contained"
            autoFocus
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
